import {computed, ref} from "vue";
import {defineStore} from "pinia";
import {getItem, removeItem, saveItem} from "@/shared/utils/store.ts";
import {login, refreshSession} from "@/features/auth/services/authService.ts";
import {LoginRes} from "@/shared/types/index.ts";

export const useSessionStore = defineStore('session', () => {
  const idToken = ref<string>(getItem('id_token'));
  const accessToken = ref<string>(getItem('access_token'));
  const refreshToken = ref<string>(getItem('refresh_token'));
  const tenantID = ref<string>(getItem('tenant_id'));
  const userID = ref<string>(getItem('user_id'));
  const loading = ref(false);
  const error = ref<unknown>(null);

  // Action to save the user session
  const saveSession = (id: string, access: string, refresh: string) => {
    const decoded = decodeToken(id);

    idToken.value = id;
    accessToken.value = access;
    refreshToken.value = refresh;

    const businessID = decoded['custom:custom:tenant'];
    const itemID = decoded['cognito:username'];
    if (businessID) {
      tenantID.value = businessID;
    }

    if (itemID) {
      userID.value = itemID;
    }

    saveItem('tenant_id', businessID);
    saveItem('id_token', id);
    saveItem('access_token', access);
    saveItem('refresh_token', refresh);
  };

  // Action to clear the user session (logout)
  const clearSession = () => {
    idToken.value = '';
    accessToken.value = '';
    refreshToken.value = '';

    removeItem('id_token');
    removeItem('access_token');
    removeItem('refresh_token');
    removeItem('tenant_id');
  };

  // Decode the token to get the payload
  const decodeToken = (token: string | null) => {
    if (!token) return null;
    const payload = token.split(".")[1];
    if (!payload) return null;
    try {
      return JSON.parse(atob(payload));
    } catch (error) {
      return null;
    }
  };

  // Computed property to get the username from idToken
  const profile = computed(() => {
    const decoded = decodeToken(idToken.value);
    return {
      username: decoded ? decoded.username || decoded["cognito:username"] : null,
      photo: decoded ? decoded['custom:profile_image'] : null,
    };
  });

  const isAuthenticated = computed(() => !!idToken.value);

  // call to backend
  const fetchLogin = async (username: string, password: string) => {
    loading.value = true;
    error.value = null;
    try {
      const result: LoginRes = await login({username, password});
      saveSession(result.id_token, result.access_token, result.refresh_token);
    } catch (e) {
      error.value = e;
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const fetchRefreshSession = async (): Promise<string> => {
    loading.value = true;
    error.value = null;
    try {
      const result: LoginRes = await refreshSession({refresh_token: refreshToken.value});
      saveSession(result.id_token, result.access_token, result.refresh_token);
      return result.id_token;
    } catch (e) {
      error.value = e;
      throw e;
    } finally {
      loading.value = false;
    }
  };

  return {
    tenantID,
    userID,
    idToken,
    accessToken,
    refreshToken,
    loading,
    error,
    clearSession,
    isAuthenticated,
    profile,
    fetchLogin,
    fetchRefreshSession,
  };
});
